import { apiPostRequest } from "helpers/Requests";
import {
  URL_ROSTERING_GEOCODE_ADDRESS,
  URL_ROSTERING_GET_NEARBY_WORKERS,
} from "store/services/URL";
import { getWorkerUuid } from "./advanceModeSplitDefaults";
import { isValidShiftCoordinate } from "./advanceModeShiftLocation";

export const formatNearbyWorkerDistance = (distance) => {
  if (distance === undefined || distance === null) return "";
  if (distance < 1) {
    return `${Math.round(distance * 1000)}m`;
  }
  return `${distance.toFixed(1)}km`;
};

export const enhanceWorkerOptionsWithNearbyDistance = (
  workerOptions = [],
  nearbyWorkers = []
) => {
  if (!workerOptions.length) return workerOptions;

  const nearbyWorkersMap = new Map();
  nearbyWorkers.forEach((worker) => {
    if (worker?.uuid) {
      nearbyWorkersMap.set(worker.uuid, worker.distance);
    }
  });

  const enhanced = workerOptions.map((option) => {
    const uuid = getWorkerUuid(option);
    const distance = uuid ? nearbyWorkersMap.get(uuid) : undefined;

    if (distance !== undefined && distance !== null) {
      return {
        ...option,
        label: `${option.label} (${formatNearbyWorkerDistance(distance)})`,
        distance,
      };
    }

    return {
      ...option,
      distance: null,
    };
  });

  return enhanced.sort((a, b) => {
    const aHasDistance = a.distance !== undefined && a.distance !== null;
    const bHasDistance = b.distance !== undefined && b.distance !== null;

    if (aHasDistance && bHasDistance) {
      return a.distance - b.distance;
    }
    if (aHasDistance && !bHasDistance) return -1;
    if (!aHasDistance && bHasDistance) return 1;

    return (a.label || "").localeCompare(b.label || "");
  });
};

export const resolveShiftCoordinates = async (shiftLocation, options = {}) => {
  const lat = shiftLocation?.lat;
  const lng = shiftLocation?.lng;

  if (isValidShiftCoordinate(lat, lng)) {
    return { lat: Number(lat), lng: Number(lng) };
  }

  if (!shiftLocation?.street && !shiftLocation?.suburb) {
    return null;
  }

  try {
    const geocodeResponse = await apiPostRequest(
      URL_ROSTERING_GEOCODE_ADDRESS,
      {
        street: shiftLocation.street || "",
        suburb: shiftLocation.suburb || "",
        state: shiftLocation.state_name || shiftLocation.state || "",
        postcode: shiftLocation.postcode || "",
      },
      null,
      options.signal ? { signal: options.signal } : {}
    );

    const gLat = geocodeResponse?.data?.coordinates?.lat;
    const gLng = geocodeResponse?.data?.coordinates?.lng;

    if (isValidShiftCoordinate(gLat, gLng)) {
      return { lat: Number(gLat), lng: Number(gLng) };
    }
  } catch {
    // Geocoding failed or cancelled
  }

  return null;
};

export const extractNearbyWorkersFromResponse = (response) => {
  const workers =
    response?.data?.nearby_workers?.workers ??
    response?.data?.data?.nearby_workers?.workers ??
    response?.data?.workers;
  return Array.isArray(workers) ? workers : [];
};

/** Call nearby-workers for common-resource worker UUIDs (advance mode). */
export const fetchNearbyWorkersForIds = async (
  shiftLocation,
  workerIds = [],
  options = {}
) => {
  const ids = workerIds.filter(Boolean);
  if (!ids.length) return [];

  const coords = await resolveShiftCoordinates(shiftLocation, options);
  if (!coords) return [];
  if (options.signal?.aborted) return [];

  const response = await apiPostRequest(
    URL_ROSTERING_GET_NEARBY_WORKERS,
    {
      lat: coords.lat,
      lng: coords.lng,
      worker_ids: ids,
      max_distance: 10000,
      limit: 10000,
    },
    null,
    options.signal ? { signal: options.signal } : {}
  );

  return extractNearbyWorkersFromResponse(response);
};

export const buildAdvanceNearbyFetchKey = (workerOptions, shiftLocation) => {
  const workerIds = (workerOptions || [])
    .map((w) => getWorkerUuid(w))
    .filter(Boolean)
    .sort()
    .join(",");

  const locationKey = [
    shiftLocation?.lat ?? "",
    shiftLocation?.lng ?? "",
    shiftLocation?.street ?? "",
    shiftLocation?.suburb ?? "",
    shiftLocation?.postcode ?? "",
    shiftLocation?.state_name ?? shiftLocation?.state ?? "",
  ].join("|");

  return `${workerIds}|${locationKey}`;
};
